import { avatarColor, initials } from "./_shared";

export type PresencePeer = {
  actingUserId: number;
  name: string;
  avatarUrl: string | null;
  state: string;
};

function Avatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="h-5 w-5 rounded-full object-cover ring-1 ring-white"
      />
    );
  }
  return (
    <div
      className={`h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white ring-1 ring-white ${avatarColor(name)}`}
    >
      {initials(name)}
    </div>
  );
}

function formatNames(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

export function PresenceIndicator({ peers }: { peers: PresencePeer[] }) {
  if (peers.length === 0) return null;

  const typists = peers.filter((p) => p.state === "typing");
  const viewers = peers.filter((p) => p.state !== "typing");

  const shown = typists.length > 0 ? typists : viewers;
  const isTyping = typists.length > 0;
  const label = isTyping
    ? shown.length === 1
      ? "is typing…"
      : "are typing…"
    : shown.length === 1
    ? "is viewing"
    : "are viewing";

  return (
    <div
      className="flex items-center gap-1.5 text-xs text-gray-500"
      data-testid="presence-indicator"
    >
      <div className="flex -space-x-1">
        {shown.slice(0, 3).map((p) => (
          <Avatar key={p.actingUserId} name={p.name} avatarUrl={p.avatarUrl} />
        ))}
      </div>
      <span className={isTyping ? "italic" : ""}>
        {formatNames(shown.slice(0, 2).map((p) => String(p.name ?? "").split(" ")[0]))} {label}
      </span>
    </div>
  );
}
